import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import { AvatarGroup } from "../../components/DashboardComponents/AvatarGroup"
import ListingTabs from "../../components/Listing/ListingTabs"
import InfiniteListSentinel from "../../components/Listing/InfiniteListSentinel"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../components/Common/EmptyState"
import MediaFallbackImage from "../../components/Common/MediaFallbackImage"
import DeleteConfirmation from "../../components/Common/DeleteConfirmation"
import CreateCallModal from "./CreateCallModal"
import { ICONS } from "../../constants/icons"
import { Bounce, toast } from "react-toastify"
import { del, get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import useInfinitePagination from "../../hooks/useInfinitePagination"
import {
  CALL_LIST_WITH,
  getCallStatusLabel,
  getCallTypeBadgeClass,
  getCallTypeBadgeLabel,
  getListStatusBadgeClass,
  mapApiCall,
  statusTone,
} from "./callMappers"

const CallCardSkeleton = () => (
  <div
    className="calls-for-artist__card calls-for-artist__card--skeleton"
    aria-hidden="true"
  >
    <div className="amok-skeleton calls-for-artist__skeleton-image" />
    <div className="calls-for-artist__skeleton-body">
      <div className="amok-skeleton calls-for-artist__skeleton-line calls-for-artist__skeleton-line--sm" />
      <div className="amok-skeleton calls-for-artist__skeleton-line calls-for-artist__skeleton-line--md" />
      <div className="amok-skeleton calls-for-artist__skeleton-line" />
      <div className="amok-skeleton calls-for-artist__skeleton-line calls-for-artist__skeleton-line--footer" />
    </div>
  </div>
)

const PAGE_SIZE = 10
const SKELETON_ROWS = 4
const LOAD_MORE_SKELETON_ROWS = 2

/** API statuses → list tabs */
const TABS = [
  { key: "published", label: "Published" },
  { key: "pending", label: "Under Review" },
  { key: "rejected", label: "Rejected" },
  { key: "closed", label: "Closed" },
]

const EMPTY_TAB_COUNTS = TABS.reduce(
  (acc, tab) => ({ ...acc, [tab.key]: 0 }),
  {},
)

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const canViewCall = status =>
  status === "published" || status === "rejected" || status === "closed"
const canDeleteCall = status => status === "published" || status === "pending"
const canEditCall = status => status === "pending"

const extractStatusCounts = result => {
  const raw =
    result?.counts ||
    result?.status_counts ||
    result?.meta?.counts ||
    result?.summary
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null

  const next = {}
  TABS.forEach(({ key }) => {
    const value = Number(
      raw[key] ?? raw[key.replace("-", "_")] ?? raw[`${key}_count`],
    )
    if (Number.isFinite(value)) next[key] = value
  })

  return Object.keys(next).length > 0 ? next : null
}

const CallsForArtist = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("published")
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [calls, setCalls] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [tabCounts, setTabCounts] = useState(EMPTY_TAB_COUNTS)
  const [editingCall, setEditingCall] = useState(null)
  const [deletingCall, setDeletingCall] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  document.title = "Inboxes & Calls for Artists | AMOK . EARTH"

  const fetchTabCounts = useCallback(async () => {
    try {
      const entries = await Promise.all(
        TABS.map(async ({ key }) => {
          const response = await get(endpoints.getCalls, {
            params: {
              page: 1,
              per_page: 1,
              status: key,
              search: "",
              type: "",
            },
            skipErrorToast: true,
          })
          const total = Number(response?.data?.result?.total ?? 0)
          return [key, Number.isFinite(total) ? Math.max(0, total) : 0]
        }),
      )
      setTabCounts(Object.fromEntries(entries))
    } catch {
      // Keep last known counts; list errors surface separately.
    }
  }, [])

  const fetchCalls = useCallback(
    async ({ page: pageToLoad = 1, append = false } = {}) => {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setError(null)
      }

      try {
        const params = {
          page: pageToLoad,
          per_page: PAGE_SIZE,
          status: activeTab,
          search: "",
          type: "",
          "with[]": CALL_LIST_WITH,
        }

        const response = await get(endpoints.getCalls, { params })

        if (response?.data?.success) {
          const result = response.data.result || {}
          const list = Array.isArray(result) ? result : result.data || []
          const rows = list.map(mapApiCall)
          const total = Number(result.total ?? rows.length)
          const lastPage = Math.max(
            1,
            Number(result.last_page || Math.ceil(total / PAGE_SIZE) || 1),
          )
          setCalls(prev => (append ? [...prev, ...rows] : rows))
          setPage(pageToLoad)
          setHasMore(pageToLoad < lastPage)
          const statusCounts = extractStatusCounts(result)
          if (statusCounts) {
            setTabCounts(prev => ({ ...prev, ...statusCounts }))
          } else if (!append) {
            setTabCounts(prev => ({
              ...prev,
              [activeTab]: Number.isFinite(total) ? Math.max(0, total) : 0,
            }))
          }
        } else {
          const message = toastApiError(response, "Failed to load calls")
          setError(message)
          if (!append) {
            setCalls([])
            setHasMore(false)
          }
        }
      } catch (err) {
        const message = toastApiError(err, "Failed to load calls")
        setError(message)
        if (!append) {
          setCalls([])
          setHasMore(false)
        }
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [activeTab],
  )

  useEffect(() => {
    fetchCalls({ page: 1, append: false })
  }, [fetchCalls])

  useEffect(() => {
    fetchTabCounts()
  }, [fetchTabCounts])

  const tabItems = useMemo(
    () =>
      TABS.map(tab => ({
        ...tab,
        count: tabCounts[tab.key] ?? 0,
      })),
    [tabCounts],
  )

  const handleTabChange = key => {
    if (key === activeTab) return
    setActiveTab(key)
    setPage(1)
    setHasMore(false)
  }

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) return
    fetchCalls({ page: page + 1, append: true })
  }, [fetchCalls, hasMore, isLoading, isLoadingMore, page])

  const { sentinelRef } = useInfinitePagination({
    hasMore: hasMore && !isLoading,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    resetKey: `${activeTab}-${calls.length}`,
  })

  const openCall = id => {
    if (!id) return
    navigate(`/calls-for-artist/${id}`)
  }

  const handleCreateClose = () => {
    setIsCreateOpen(false)
    setEditingCall(null)
  }

  const handleEditCall = call => {
    if (!call?.id) return
    setEditingCall(call)
    setIsCreateOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingCall?.id) return
    setIsDeleting(true)
    try {
      const response = await del(endpoints.deleteCall(deletingCall.id))
      if (!response?.data?.success) return
      toast.success(response.data.message || "Call deleted", TOAST_OPTIONS)
      setDeletingCall(null)
      await Promise.all([
        fetchCalls({ page: 1, append: false }),
        fetchTabCounts(),
      ])
    } catch (err) {
      toastApiError(err, "Failed to delete call")
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="page-content">
      <div className="calls-for-artist">
        <PageHeader
          className="calls-for-artist__header"
          title="Inboxes & Calls for Artists"
          subtitle="Looking for a drummer or a supporting band? Technical feedback on cameras or recording software? Ask your city by creating an Inbox!"
          actions={
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={() => {
                setEditingCall(null)
                setIsCreateOpen(true)
              }}
            >
              Create New
            </CustomButton>
          }
        />

        <div className="calls-for-artist__tabs">
          <ListingTabs
            items={tabItems}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </div>

        <div
          className="calls-for-artist__list"
          aria-busy={isLoading || isLoadingMore || undefined}
        >
          {isLoading &&
            Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <CallCardSkeleton key={`skeleton-${index}`} />
            ))}

          {!isLoading &&
            calls.map(call => {
              const people = call.responsePeople || []
              const typeLabel = getCallTypeBadgeLabel(call.type)
              const showView = canViewCall(call.status)
              const showDelete = canDeleteCall(call.status)
              const showEdit = canEditCall(call.status)

              return (
                <article
                  key={call.id}
                  className={`calls-for-artist__card calls-for-artist__card--${statusTone(
                    call.status,
                  )}${showView ? " calls-for-artist__card--clickable" : ""}`}
                  onClick={showView ? () => openCall(call.id) : undefined}
                  onKeyDown={
                    showView
                      ? e => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            openCall(call.id)
                          }
                        }
                      : undefined
                  }
                  role={showView ? "button" : undefined}
                  tabIndex={showView ? 0 : undefined}
                >
                  <MediaFallbackImage
                    src={call.image}
                    fallback={ICONS.thumbnailPlaceholder}
                    alt=""
                    className="calls-for-artist__image"
                  />

                  <div className="calls-for-artist__content">
                    <div className="calls-for-artist__top">
                      <div className="calls-for-artist__copy">
                        <p className="calls-for-artist__project">
                          {call.projectName || call.title}
                        </p>
                        <h3 className="calls-for-artist__headline">
                          {call.headline || "N/A"}
                        </h3>
                        <p className="calls-for-artist__desc">
                          {call.description}
                        </p>
                      </div>

                      <div className="calls-for-artist__status-col">
                        {typeLabel ? (
                          <span
                            className={`calls-for-artist__badge ${getCallTypeBadgeClass(
                              call.type,
                            )}`}
                          >
                            {typeLabel}
                          </span>
                        ) : null}
                        <span
                          className={`calls-for-artist__badge ${getListStatusBadgeClass(
                            call,
                          )}`}
                        >
                          {getCallStatusLabel(call)}
                        </span>
                        {call.deadline ? (
                          <span className="calls-for-artist__deadline">
                            {call.deadline}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="calls-for-artist__footer">
                      <div className="calls-for-artist__meta">
                        <span className="calls-for-artist__location">
                          <img src={ICONS.city} alt="" width={15} height={15} />
                          {call.city}
                        </span>
                        {(call.categoryNames || []).map(name => (
                          <span
                            key={name}
                            className="calls-for-artist__category"
                          >
                            {name}
                          </span>
                        ))}
                        <AvatarGroup
                          people={people}
                          size="xs"
                          max={3}
                          className="calls-for-artist__avatars"
                        />
                        <span className="calls-for-artist__responses-count">
                          {call.responses} responses
                        </span>
                      </div>

                      <div className="calls-for-artist__actions">
                        {showView ? (
                          <CustomButton
                            variant="primary"
                            size="sm"
                            className="calls-for-artist__cta"
                            trailingIconSrc={ICONS.arrowRight}
                            trailingIconAlt=""
                            onClick={e => {
                              e.stopPropagation()
                              openCall(call.id)
                            }}
                          >
                            View Detail
                          </CustomButton>
                        ) : null}
                        {showEdit ? (
                          <CustomButton
                            variant="primary"
                            size="sm"
                            className="calls-for-artist__cta"
                            trailingIconSrc={ICONS.edit}
                            trailingIconAlt=""
                            onClick={e => {
                              e.stopPropagation()
                              handleEditCall(call)
                            }}
                          >
                            Edit
                          </CustomButton>
                        ) : null}
                        {showDelete ? (
                          <CustomIconButton
                            iconSrc={ICONS.trash}
                            label="Delete"
                            tone="danger"
                            size="sm"
                            onClick={e => {
                              e.stopPropagation()
                              setDeletingCall(call)
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </article>
              )
            })}

          {!isLoading &&
            isLoadingMore &&
            Array.from({ length: LOAD_MORE_SKELETON_ROWS }).map((_, index) => (
              <CallCardSkeleton key={`more-skeleton-${index}`} />
            ))}

          {!isLoading && !isLoadingMore && calls.length === 0 && (
            <EmptyState
              title={error ? "Unable to load calls" : "No calls found"}
              description={
                error ||
                "Nothing in this tab yet. Create a new call to get started."
              }
              actionText="Create New"
              onActionClick={() => setIsCreateOpen(true)}
            />
          )}
        </div>

        <InfiniteListSentinel
          sentinelRef={sentinelRef}
          hasMore={hasMore && !isLoading}
        />

        <CreateCallModal
          isOpen={isCreateOpen}
          onClose={handleCreateClose}
          editCall={editingCall}
          onSubmit={async payload => {
            if (payload?.isEditing) {
              await Promise.all([
                fetchCalls({ page: 1, append: false }),
                fetchTabCounts(),
              ])
              return
            }
            const nextTab = payload?.type === "formal" ? "pending" : "published"
            await fetchTabCounts()
            if (nextTab !== activeTab) {
              setActiveTab(nextTab)
            } else {
              await fetchCalls({ page: 1, append: false })
            }
          }}
        />

        <DeleteConfirmation
          isOpen={Boolean(deletingCall)}
          onCancel={() => !isDeleting && setDeletingCall(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete call"
          message="Are you sure you want to delete this call? This action cannot be undone."
          cancelText="Cancel"
          confirmText="Delete"
          isDeleting={isDeleting}
        />
      </div>
    </div>
  )
}

export default CallsForArtist
